import { ReloadIcon } from "@radix-ui/react-icons";
import { Button, Flex, Text } from "@radix-ui/themes";
import { usePostHog } from "@posthog/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrandLogo } from "../../components/brand-logo";
import { LangSelector } from "./lang-selector";

const AUTO_RELOAD_AFTER_MS = 5 * 60_000;
const MAX_AUTO_RELOADS = 3;
const AUTO_RELOAD_COUNT_KEY = "kiosk:maintenance-auto-reloads";

// Module scope keeps this identity stable across renders. Built inline as a
// parameter default it would be a new function every render, and since the
// countdown effect depends on it, every parent re-render would re-arm the timer
// from zero. The parent re-renders on each service poll, far more often than
// every five minutes, so the countdown would never reach the end.
const reloadPage = () => window.location.reload();

export function MaintenanceMode({
  reload = reloadPage,
}: {
  reload?: () => void;
} = {}) {
  const { t } = useTranslation();
  const posthog = usePostHog();

  /**
   * Last resort for a kiosk that reaches maintenance mode and cannot leave it.
   * Every in-app recovery path depends on a service signal still arriving, so
   * when one stops arriving entirely a reload is the only remaining lever, and
   * nobody is on site at 4 AM to press the button below. Bounded per tab, and
   * reset on unmount, so a store that is genuinely closed for maintenance
   * settles instead of reloading all night.
   */
  useEffect(() => {
    const attempts = Number(
      sessionStorage.getItem(AUTO_RELOAD_COUNT_KEY) ?? "0",
    );
    // A capped tab schedules nothing but must still register the cleanup below,
    // otherwise the counter stays at its limit and every later maintenance
    // incident in this tab is permanently ineligible for auto-reload.
    const timer =
      attempts >= MAX_AUTO_RELOADS
        ? undefined
        : window.setTimeout(() => {
            sessionStorage.setItem(AUTO_RELOAD_COUNT_KEY, String(attempts + 1));
            posthog.capture("kiosk_maintenance_auto_reload", {
              attempt: attempts + 1,
              stuck_for_ms: AUTO_RELOAD_AFTER_MS,
              online: navigator.onLine,
            });
            reload();
          }, AUTO_RELOAD_AFTER_MS);

    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      // Cleanup does not run when the reload above tears the page down, so the
      // counter only clears on a real recovery or on staff intervention.
      sessionStorage.removeItem(AUTO_RELOAD_COUNT_KEY);
    };
  }, [posthog, reload]);

  return (
    <main className="p-6 mobile-fa bg-white h-dvh overflow-hidden select-none">
      <Flex direction="column" className="h-full">
        <Flex justify="between" mb="4">
          <BrandLogo />
          <LangSelector />
        </Flex>
        <Flex
          direction="column"
          flexGrow="1"
          gap="2"
          align="center"
          justify="center"
          className="pb-20"
        >
          <Flex
            className="w-[600px] h-[400px] overflow-hidden"
            align="center"
            justify="center"
          >
            <img
              src="/unmanned/maintenance.webp"
              className="size-[500px]"
              alt=""
            />
          </Flex>
          <Text className="font-bold !text-[42px] leading-">
            {t("maintenanceTitle")}
          </Text>
          <Text size="7" as="p" align="center">
            {t("maintenanceText1")}
          </Text>
          <Text size="7" as="p" align="center" mb="2">
            {t("maintenanceText2")}
          </Text>
          <Text size="6" as="p" align="center">
            {t("maintenanceText3")}
          </Text>
          <Flex mt="3">
            <Button
              size="4"
              highContrast
              color="gray"
              radius="large"
              className="!min-h-14 !px-16 !rounded-2xl"
              onClick={reload}
            >
              <ReloadIcon />
              {t("refreshPage")}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </main>
  );
}
